import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { intl } from "LocaleWrapper";
import { Button, PageHeader } from "@cofano/cck";
import Helmet from "components/helmet/Helmet";
import WidgetGrid from "view/dashboard/components/WidgetGrid";
import { useWidgets } from "view/dashboard/utils/useWidgets";
import WidgetBuilderDialog from "view/dashboard/components/dialogs/WidgetBuilderDialog";
import WidgetOrderDialog from "view/dashboard/components/dialogs/WidgetOrderDialog";
import WidgetSaveDefaultDialog from "view/dashboard/components/dialogs/WidgetSaveDefaultDialog";
import { usePermission } from "components/utils/Permission";
import { useConfirm } from "components/hooks/useConfirm";
import { WidgetTemplate } from "view/dashboard/types/widget";

const Dashboard = () => {

	const { widgets, save, remove, reorder, reset, isOwn } = useWidgets([]);
	const [ building, setBuilding ] = useState(false);
	const [ editing, setEditing ] = useState<WidgetTemplate>();
	const [ ordering, setOrdering ] = useState(false);
	const [ savingDefault, setSavingDefault ] = useState(false);

	const { isAdmin, isCompanyUser } = usePermission();
	const confirm = useConfirm();

	const confirmReset = () => {
		confirm({
			type: "WARNING",
			title: intl.formatMessage({ id: "WIDGET_RESET_TITLE" }),
			content: <>{intl.formatMessage({ id: "WIDGET_RESET_CONTENT" })}</>,
			confirmText: intl.formatMessage({ id: "RESET" }),
		})
			.then(() => reset())
			.catch(() => { /* cancelled */ });
	};

	const edit = (template: WidgetTemplate) => {
		setEditing(template);
		setBuilding(true);
	};

	const close = () => {
		setBuilding(false);
		setEditing(undefined);
	};

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					<FormattedMessage id='DASHBOARD' />
					<Helmet title={intl.formatMessage({ id: "DASHBOARD" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{isOwn && (
						<Button variant="text" onClick={confirmReset}>
							{intl.formatMessage({ id: "RESET" })}
						</Button>
					)}

					{(isAdmin || isCompanyUser) && (
						<Button
							variant="outlined"
							disabled={widgets.length === 0}
							onClick={() => setSavingDefault(true)}>
							{intl.formatMessage({ id: "WIDGET_SAVE_FOR_ORGANIZATION" })}
						</Button>
					)}

					<Button
						variant="outlined"
						disabled={widgets.length < 2}
						onClick={() => setOrdering(true)}>
						{intl.formatMessage({ id: "CHANGE_ORDER" })}
					</Button>

					<Button
						onClick={() => {
							setEditing(undefined);
							setBuilding(true);
						}}>
						{intl.formatMessage({ id: "WIDGET_ADD" })}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<WidgetGrid widgets={widgets} onEdit={edit} onDelete={remove} />

			<WidgetBuilderDialog
				open={building}
				onClose={close}
				template={editing}
				order={widgets.length}
				onSave={save} />

			<WidgetSaveDefaultDialog
				open={savingDefault}
				onClose={() => setSavingDefault(false)}
				widgets={widgets} />

			<WidgetOrderDialog
				open={ordering}
				onClose={() => setOrdering(false)}
				widgets={widgets}
				onSave={reorder} />
		</div>
	);
};

export default Dashboard;
